// /shared/hooks/useHasBreedConditions.tsx
import { useMemo } from "react";
import { Species } from "@spotpetinsurance/spot-types/entities/Species";
import { AffectedBreed, getAccidentCoverageItems, getIllnessSubSections } from "@/shared/components/FullCoverageDetailsModal/coverageData";
import { Policy } from "@/shared/types/Quote.interface";
import Strings from "../utils/Strings.constants";

interface UseHasBreedConditionsProps {
    isMultiPetLinked?: boolean;
    policies?: Policy[];
    activePetBreed?: string;
    species?: Species;
    hasAccidentAndIllness?: boolean;
    isDentalCovered?: boolean;
    enabled?: boolean;
}

export interface BreedConditionData {
    breedName: string;
    claimCondition?: string;
    totalClaims: number;
    avgClaimAmount: number | null;
}

const getPluralBreedName = (breedName: string): string => {
    const mapping = Strings.PTZ_US.BREED_DISPLAY_MAPPINGS.find(mapping => breedName.includes(mapping.breedLabel));
    return mapping ? mapping.pluralLabel : breedName;
};

export const useHasBreedConditions = ({
    isMultiPetLinked,
    policies,
    activePetBreed,
    species,
    hasAccidentAndIllness,
    isDentalCovered,
    enabled = true
}: UseHasBreedConditionsProps) => {
    const result = useMemo(() => {
        if (!enabled) return { hasConditions: false, breedData: null };

        // Get the target breed name using the same logic
        const targetBreed = isMultiPetLinked ? policies?.[0]?.breedName : activePetBreed;
        if (!targetBreed) return { hasConditions: false, breedData: null };

        // Helper function to check if breed matches any affects data
        const breedMatchesAffects = (affects: any[]) => {
            return affects.some((affectData: any) => targetBreed.includes(affectData.breedName));
        };

        // Get accident items using the same filtering logic as CoverageIconCategories
        const allAccidentItems = getAccidentCoverageItems(species ?? Species.Dog, "size-12", hasAccidentAndIllness ?? false);

        let filteredAccidentItems;
        if (enabled && hasAccidentAndIllness) {
            // Apply the same filtering logic as getFilteredAccidentItems
            const matchingBreedItems = allAccidentItems.filter(item => {
                if (!item.affects || !item.replaces) return false;
                return breedMatchesAffects(item.affects);
            });

            const replacedIds = matchingBreedItems.filter(item => item.replaces).flatMap(item => item.replaces);
            const defaultItems = allAccidentItems.filter(item => !item.replaces && !replacedIds.includes(item.id));

            filteredAccidentItems = [...defaultItems, ...matchingBreedItems];
        } else {
            // Show only defaults when disabled OR accident-only
            filteredAccidentItems = allAccidentItems.filter(item => !item.replaces);
        }

        // Check if any of the filtered accident items have breed conditions
        const hasAccidentBreedConditions = filteredAccidentItems.some((item: any) => {
            if (!item.affects) return false;
            return breedMatchesAffects(item.affects);
        });

        // Check illness sections only if user has accident + illness coverage
        let hasIllnessBreedConditions = false;
        const illnessSubSections = hasAccidentAndIllness ? getIllnessSubSections() : {};

        if (hasAccidentAndIllness) {
            // Remove dental if not covered
            if (!isDentalCovered) {
                delete illnessSubSections.dentalIllnesses;
            }

            // Check if any illness sections or their items have breed conditions
            hasIllnessBreedConditions = Object.values(illnessSubSections).some(section => {
                // Check section-level affects
                if (section.affects && breedMatchesAffects(section.affects)) {
                    return true;
                }

                // Check item-level affects
                return section.includes?.some((item: any) => {
                    if (!item.affects) return false;
                    return breedMatchesAffects(item.affects);
                });
            });
        }

        if (hasAccidentBreedConditions || hasIllnessBreedConditions) {
            const potentialConditions: (AffectedBreed & { conditionTitle: string })[] = [];

            filteredAccidentItems.forEach(item => {
                if (item.affects) {
                    const match = item.affects.find(affect => targetBreed.includes(affect.breedName));
                    if (match) {
                        potentialConditions.push({ ...(match as AffectedBreed), conditionTitle: item.title });
                    }
                }
            });

            if (hasAccidentAndIllness) {
                Object.values(illnessSubSections).forEach(section => {
                    if (section.affects) {
                        const match = section.affects.find(affect => targetBreed.includes(affect.breedName));
                        if (match) {
                            potentialConditions.push({ ...(match as AffectedBreed), conditionTitle: section.title });
                        }
                    }

                    section.includes?.forEach(item => {
                        if (item.affects) {
                            const match = item.affects.find(affect => targetBreed.includes(affect.breedName));
                            if (match) {
                                potentialConditions.push({ ...(match as AffectedBreed), conditionTitle: item.title });
                            }
                        }
                    });
                });
            }

            let primaryCondition: (AffectedBreed & { conditionTitle: string }) | null = null;
            if (potentialConditions.length > 0) {
                // First, find the best condition based on claim frequency
                primaryCondition = potentialConditions.reduce((prev, current) => {
                    return (prev.claimFrequency || 0) > (current.claimFrequency || 0) ? prev : current;
                });

                // If no condition with a claim frequency was found (i.e., best is 0),
                // try finding one by the highest average claim amount instead.
                const bestByFrequency = primaryCondition?.claimFrequency || 0;
                if (bestByFrequency === 0) {
                    const bestByAmount = potentialConditions.reduce((prev, current) => {
                        return (prev.avgClaimAmount || 0) > (current.avgClaimAmount || 0) ? prev : current;
                    });

                    // Only use this result if it actually has a claim amount
                    if ((bestByAmount?.avgClaimAmount || 0) > 0) {
                        primaryCondition = bestByAmount;
                    }
                }
            }

            if (primaryCondition) {
                const finalData: BreedConditionData = {
                    breedName: getPluralBreedName(targetBreed),
                    claimCondition: primaryCondition.conditionTitle,
                    totalClaims: primaryCondition.claimFrequency || 0,
                    avgClaimAmount: primaryCondition.avgClaimAmount || null
                };

                return {
                    hasConditions: true,
                    breedData: finalData
                };
            }
        }

        return { hasConditions: false, breedData: null };
    }, [enabled, isMultiPetLinked, policies, activePetBreed, species, hasAccidentAndIllness, isDentalCovered]);

    return result;
};
